import React, { useState, useRef, useEffect } from 'react'

/**
 * Validate Remarks2 section: when Remarks text is entered, Block * and Item * must be selected.
 * Use this before save in parent to block save until user selects Block and Item.
 * @param {{ remarksBlock?: string, remarksItems?: string, remarks?: string }} remarksData
 * @returns {{ valid: boolean, message?: string }}
 */
export function validateRemarks2Section(remarksData) {
  if (!remarksData) return { valid: true };
  const hasRemarksText = Boolean(remarksData.remarks && String(remarksData.remarks).trim());
  if (!hasRemarksText) return { valid: true };

  const isBlockSelected = Boolean(
    remarksData.remarksBlock &&
    String(remarksData.remarksBlock).trim() !== '' &&
    remarksData.remarksBlock !== 'SELECT_BLOCK'
  );
  const isItemSelected = Boolean(
    remarksData.remarksItems &&
    String(remarksData.remarksItems).trim() !== '' &&
    remarksData.remarksItems !== 'SELECT_ITEM'
  );

  if (!isBlockSelected) return { valid: false, message: 'Please select Block when Remarks are entered.' };
  if (!isItemSelected) return { valid: false, message: 'Please select Item when Remarks are entered.' };
  return { valid: true };
}

function Remarks2({
  remarksData,
  setRemarksData,
  blockOptions = [], // Array of block options like ['K', 'L'] or ['BLOCK K', 'BLOCK L']
  itemOptions = [], // Array of item options (strings)
  remarksList = []
}) {
  const [isBlockDropdownOpen, setIsBlockDropdownOpen] = useState(false);
  const [isItemDropdownOpen, setIsItemDropdownOpen] = useState(false);
  const [blockSearchTerm, setBlockSearchTerm] = useState('');
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [selectedBlockIndex, setSelectedBlockIndex] = useState(-1);
  const [selectedItemIndex, setSelectedItemIndex] = useState(-1);
  const blockDropdownRef = useRef(null);
  const itemDropdownRef = useRef(null);
  const justOpenedBlockRef = useRef(false);
  const justOpenedItemRef = useRef(false);
  const touchStartRef = useRef(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setRemarksData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle Block dropdown – "Select Block" / empty = no selection so payload sends empty
  const handleBlockSelect = (block) => {
    const value = (block === 'SELECT_BLOCK' || block === '') ? '' : block;
    setRemarksData(prev => ({
      ...prev,
      remarksBlock: value
    }));
    setBlockSearchTerm('');
    setIsBlockDropdownOpen(false);
    setSelectedBlockIndex(-1);
  };

  const openBlockDropdown = () => {
    setIsBlockDropdownOpen(true);
    justOpenedBlockRef.current = true;
    setTimeout(() => {
      justOpenedBlockRef.current = false;
    }, 300);
  };

  const closeBlockDropdown = () => {
    setIsBlockDropdownOpen(false);
    setSelectedBlockIndex(-1);
  };

  const toggleBlockDropdown = () => {
    if (!justOpenedBlockRef.current) {
      setIsBlockDropdownOpen(prev => !prev);
    }
  };

  // Handle Item dropdown
  const handleItemSelect = (item) => {
    // If "Select Item" is selected, clear the item
    if (item === 'SELECT_ITEM') {
      setRemarksData(prev => ({
        ...prev,
        remarksItems: ''
      }));
    } else {
      setRemarksData(prev => ({
        ...prev,
        remarksItems: item
      }));
    }
    setItemSearchTerm('');
    setIsItemDropdownOpen(false);
    setSelectedItemIndex(-1);
  };

  const openItemDropdown = () => {
    setIsItemDropdownOpen(true);
    justOpenedItemRef.current = true;
    setTimeout(() => {
      justOpenedItemRef.current = false;
    }, 300);
  };

  const closeItemDropdown = () => {
    setIsItemDropdownOpen(false);
    setSelectedItemIndex(-1);
  };

  const toggleItemDropdown = () => {
    if (!justOpenedItemRef.current) {
      setIsItemDropdownOpen(prev => !prev);
    }
  };

  // Filter blocks based on search term
  const getFilteredBlocks = () => {
    // Ensure blockOptions is always an array
    const validBlockOptions = Array.isArray(blockOptions) ? blockOptions : [];
    if (!blockSearchTerm) {
      return validBlockOptions;
    }
    return validBlockOptions.filter(block =>
      block && block.toLowerCase().includes(blockSearchTerm.toLowerCase())
    );
  };

  // Filter items based on search term
  const getFilteredItems = () => {
    // Ensure itemOptions is always an array
    const validItemOptions = Array.isArray(itemOptions) ? itemOptions : [];
    if (!itemSearchTerm || itemSearchTerm.trim() === '') {
      return validItemOptions;
    }
    const searchLower = itemSearchTerm.toLowerCase().trim();
    return validItemOptions.filter(item =>
      item && String(item).toLowerCase().includes(searchLower)
    );
  };

  // Get selected block name for display
  const getSelectedBlockName = () => {
    if (isBlockDropdownOpen && blockSearchTerm) {
      return blockSearchTerm;
    }
    if (remarksData.remarksBlock === 'ALL') {
      return 'ALL';
    }
    // "Select Block" / no selection → show placeholder
    if (remarksData.remarksBlock === '' || !remarksData.remarksBlock || remarksData.remarksBlock === 'SELECT_BLOCK') {
      return '';
    }
    return remarksData.remarksBlock;
  };

  // Get selected item name for display
  const getSelectedItemName = () => {
    // If dropdown is open and user is typing, show the search term
    if (isItemDropdownOpen && itemSearchTerm) {
      return itemSearchTerm;
    }
    // If empty or "SELECT_ITEM", return empty to show placeholder
    if (remarksData.remarksItems === '' || !remarksData.remarksItems || remarksData.remarksItems === 'SELECT_ITEM') {
      return '';
    }
    // Return the selected item
    return remarksData.remarksItems;
  };

  // Handle keyboard navigation for Block dropdown
  const handleBlockKeyDown = (e) => {
    const currentSelected = selectedBlockIndex;
    const filteredList = getFilteredBlocks();
    const totalItems = filteredList.length + 2; // +1 "Select Block", +1 "ALL"

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isBlockDropdownOpen) {
          openBlockDropdown();
        }
        if (currentSelected < totalItems - 1) {
          setSelectedBlockIndex(currentSelected + 1);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!isBlockDropdownOpen) {
          openBlockDropdown();
        }
        if (currentSelected > 0) {
          setSelectedBlockIndex(currentSelected - 1);
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (isBlockDropdownOpen) {
          if (currentSelected === 0) {
            handleBlockSelect('ALL');
          } else {
            const blockIndex = currentSelected - 1;
            if (blockIndex >= 0 && blockIndex < filteredList.length) {
              handleBlockSelect(filteredList[blockIndex]);
            }
          }
        } else {
          openBlockDropdown();
        }
        break;
      case 'Escape':
        e.preventDefault();
        closeBlockDropdown();
        break;
      default:
        if (!isBlockDropdownOpen && e.key.length === 1) {
          openBlockDropdown();
        }
        break;
    }
  };

  // Handle keyboard navigation for Item dropdown
  const handleItemKeyDown = (e) => {
    const currentSelected = selectedItemIndex;
    const filteredList = getFilteredItems();
    const totalItems = filteredList.length + 1; // +1 for "Select Item" option

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isItemDropdownOpen) {
          openItemDropdown();
        }
        if (currentSelected < totalItems - 1) {
          setSelectedItemIndex(currentSelected + 1);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!isItemDropdownOpen) {
          openItemDropdown();
        }
        if (currentSelected > 0) {
          setSelectedItemIndex(currentSelected - 1);
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (isItemDropdownOpen) {
          if (currentSelected === 0) {
            handleItemSelect('SELECT_ITEM');
          } else {
            const itemIndex = currentSelected - 1;
            if (itemIndex >= 0 && itemIndex < filteredList.length) {
              handleItemSelect(filteredList[itemIndex]);
            }
          }
        } else if (!isItemDropdownOpen) {
          openItemDropdown();
        }
        break;
      case 'Escape':
        e.preventDefault();
        closeItemDropdown();
        break;
      default:
        if (!isItemDropdownOpen && e.key.length === 1) {
          openItemDropdown();
        }
        break;
    }
  };

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (blockDropdownRef.current && !blockDropdownRef.current.contains(event.target)) {
        closeBlockDropdown();
      }
      if (itemDropdownRef.current && !itemDropdownRef.current.contains(event.target)) {
        closeItemDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside, { passive: true });
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Validation: when user has entered Remarks, Block * and Item * are required
  const hasRemarksText = Boolean(remarksData?.remarks && String(remarksData.remarks).trim());
  const isBlockSelected = Boolean(
    remarksData?.remarksBlock &&
    String(remarksData.remarksBlock).trim() !== '' &&
    remarksData.remarksBlock !== 'SELECT_BLOCK'
  );
  const isItemSelected = Boolean(
    remarksData?.remarksItems &&
    String(remarksData.remarksItems).trim() !== '' &&
    remarksData.remarksItems !== 'SELECT_ITEM'
  );
  const showBlockRequiredError = hasRemarksText && !isBlockSelected;
  const showItemRequiredError = hasRemarksText && !isItemSelected;
  const showRemarksContextError = hasRemarksText && (!isBlockSelected || !isItemSelected);

  return (
    <>
      <style>
        {`
          .remarks2-custom-dropdown {
            -webkit-overflow-scrolling: touch;
            touch-action: pan-y;
          }
          .remarks2-section .position-relative {
            -webkit-tap-highlight-color: transparent;
          }
          .remarks2-section input {
            touch-action: manipulation;
          }
        `}
      </style>
      
        <div className="card">
          <div className="card-body">
            <h5 className="mb-3 border-bottom pb-3" style={{ fontWeight: 'bold' }}>Remarks</h5>
            <div className="row">
              {/* Block Field */}
              <div className="col-md-6 mb-3">
                <label htmlFor="remarksBlock">Block <span style={{ color: 'red' }}>*</span></label>
                <div className="position-relative" ref={blockDropdownRef} style={{ position: 'relative', overflow: 'visible' }}>
                  <input
                    type="text"
                    className={`form-control ${showBlockRequiredError ? 'border-danger' : ''}`}
                    id="remarksBlock"
                    name="remarksBlock"
                    value={getSelectedBlockName()}
                    placeholder="Select Block"
                    onChange={(e) => {
                      const value = e.target.value;
                      setBlockSearchTerm(value);
                      setRemarksData(prev => ({
                        ...prev,
                        remarksBlock: ''
                      }));
                    }}
                    onFocus={() => {
                      if (!isBlockDropdownOpen) {
                        openBlockDropdown();
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!justOpenedBlockRef.current) {
                        toggleBlockDropdown();
                      } else {
                        justOpenedBlockRef.current = false;
                      }
                    }}
                    onMouseDown={() => {
                      if (!isBlockDropdownOpen && !touchStartRef.current) {
                        openBlockDropdown();
                      }
                    }}
                    onTouchStart={(e) => {
                      touchStartRef.current = true;
                      setTimeout(() => {
                        touchStartRef.current = false;
                      }, 500);
                      if (!isBlockDropdownOpen) {
                        e.preventDefault();
                        openBlockDropdown();
                      }
                    }}
                    onKeyDown={handleBlockKeyDown}
                    style={{
                      paddingRight: '30px',
                      fontSize: '14px',
                      minHeight: '38px',
                      touchAction: 'manipulation'
                    }}
                  />
                  {showBlockRequiredError && (
                    <small className="text-danger d-block mt-1">Please select Block when Remarks are entered.</small>
                  )}
                  <i className="fa fa-chevron-down position-absolute"
                    style={{
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#666',
                      pointerEvents: 'none'
                    }}></i>
                  {isBlockDropdownOpen && (
                    <div
                      className="position-absolute bg-white border rounded shadow-sm remarks2-custom-dropdown"
                      style={{
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 99999,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        minWidth: '250px',
                        width: '100%',
                        maxWidth: '400px',
                        border: '1px solid #ddd',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.15)',
                        marginTop: '2px',
                        backgroundColor: '#ffffff',
                        borderRadius: '4px',
                        WebkitOverflowScrolling: 'touch',
                        touchAction: 'pan-y'
                      }}
                    >
                      {(() => {
                        const filteredBlocks = getFilteredBlocks();
                        return filteredBlocks.length > 0 ? (
                          <>
                            {/* "Select Block" = no selection → payload gets empty */}
                            <div
                              className="px-3 py-2 cursor-pointer"
                              style={{
                                cursor: 'pointer',
                                borderBottom: '1px solid #f0f0f0',
                                fontSize: '12px',
                                backgroundColor: selectedBlockIndex === 0 && !remarksData.remarksBlock ? '#e3f2fd' : 'white',
                                outline: selectedBlockIndex === 0 && !remarksData.remarksBlock ? '2px solid #1976d2' : 'none',
                                outlineOffset: '-2px',
                                touchAction: 'manipulation'
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleBlockSelect('');
                              }}
                              onTouchEnd={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleBlockSelect('');
                              }}
                              onMouseEnter={() => setSelectedBlockIndex(0)}
                              onMouseLeave={() => { if (selectedBlockIndex === 0) setSelectedBlockIndex(-1); }}
                              tabIndex={-1}
                            >
                              Select Block
                            </div>
                            {/* ALL option */}
                            <div
                              className="px-3 py-2 cursor-pointer"
                              style={{
                                cursor: 'pointer',
                                borderBottom: '1px solid #f0f0f0',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                backgroundColor: selectedBlockIndex === 1 ? '#e3f2fd' : 'white',
                                outline: selectedBlockIndex === 1 ? '2px solid #1976d2' : 'none',
                                outlineOffset: '-2px',
                                touchAction: 'manipulation'
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleBlockSelect('ALL');
                              }}
                              onTouchEnd={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleBlockSelect('ALL');
                              }}
                              onMouseEnter={() => setSelectedBlockIndex(1)}
                              onMouseLeave={() => {
                                if (selectedBlockIndex !== 1) {
                                  setSelectedBlockIndex(-1);
                                }
                              }}
                              tabIndex={-1}
                            >
                              ALL
                            </div>
                            {filteredBlocks.map((block, index) => {
                              const itemIndex = index + 2;
                              const isSelected = selectedBlockIndex === itemIndex;
                              return (
                                <div
                                  key={index}
                                  className="px-3 py-2 cursor-pointer"
                                  style={{
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f0f0f0',
                                    fontSize: '12px',
                                    backgroundColor: isSelected ? '#e3f2fd' : 'white',
                                    outline: isSelected ? '2px solid #1976d2' : 'none',
                                    outlineOffset: '-2px',
                                    touchAction: 'manipulation'
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleBlockSelect(block);
                                  }}
                                  onTouchEnd={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleBlockSelect(block);
                                  }}
                                  onMouseEnter={() => setSelectedBlockIndex(itemIndex)}
                                  onMouseLeave={() => {
                                    if (!isSelected) {
                                      setSelectedBlockIndex(-1);
                                    }
                                  }}
                                  tabIndex={-1}
                                >
                                  {block}
                                </div>
                              );
                            })}
                          </>
                        ) : (
                          <div className="px-3 py-2 text-muted" style={{ fontSize: '12px' }}>
                            No blocks found
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>

              {/* Item Field */}
              <div className="col-md-6 mb-3">
                <label htmlFor="remarksItems">Item <span style={{ color: 'red' }}>*</span></label>
                <div className="position-relative" ref={itemDropdownRef} style={{ position: 'relative', overflow: 'visible' }}>
                  <input
                    type="text"
                    className={`form-control ${showItemRequiredError ? 'border-danger' : ''}`}
                    id="remarksItems"
                    name="remarksItems"
                    value={getSelectedItemName()}
                    placeholder="Select Item"
                    onChange={(e) => {
                      const value = e.target.value;
                      setItemSearchTerm(value);
                      setRemarksData(prev => ({
                        ...prev,
                        remarksItems: ''
                      }));
                    }}
                    onFocus={() => {
                      if (!isItemDropdownOpen) {
                        openItemDropdown();
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!justOpenedItemRef.current) {
                        toggleItemDropdown();
                      } else {
                        justOpenedItemRef.current = false;
                      }
                    }}
                    onMouseDown={() => {
                      if (!isItemDropdownOpen && !touchStartRef.current) {
                        openItemDropdown();
                      }
                    }}
                    onTouchStart={(e) => {
                      touchStartRef.current = true;
                      setTimeout(() => {
                        touchStartRef.current = false;
                      }, 500);
                      if (!isItemDropdownOpen) {
                        e.preventDefault();
                        openItemDropdown();
                      }
                    }}
                    onKeyDown={handleItemKeyDown}
                    style={{
                      paddingRight: '30px',
                      fontSize: '14px',
                      minHeight: '38px',
                      touchAction: 'manipulation'
                    }}
                  />
                  {showItemRequiredError && (
                    <small className="text-danger d-block mt-1">Please select Item when Remarks are entered.</small>
                  )}
                  <i className="fa fa-chevron-down position-absolute"
                    style={{
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#666',
                      pointerEvents: 'none'
                    }}></i>
                  {isItemDropdownOpen && (
                    <div
                      className="position-absolute bg-white border rounded shadow-sm remarks2-custom-dropdown"
                      style={{
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 99999,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        minWidth: '250px',
                        width: '100%',
                        maxWidth: '400px',
                        border: '1px solid #ddd',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.15)',
                        marginTop: '2px',
                        backgroundColor: '#ffffff',
                        borderRadius: '4px',
                        WebkitOverflowScrolling: 'touch',
                        touchAction: 'pan-y'
                      }}
                    >
                      {(() => {
                        const filteredItems = getFilteredItems();
                        return (
                          <>
                            {/* Always show "Select Item" option */}
                            <div
                              className="px-3 py-2 cursor-pointer"
                              style={{
                                cursor: 'pointer',
                                borderBottom: '1px solid #f0f0f0',
                                fontSize: '12px',
                                backgroundColor: selectedItemIndex === 0 ? '#e3f2fd' : 'white',
                                outline: selectedItemIndex === 0 ? '2px solid #1976d2' : 'none',
                                outlineOffset: '-2px',
                                fontWeight: 'bold',
                                touchAction: 'manipulation'
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleItemSelect('SELECT_ITEM');
                              }}
                              onTouchEnd={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleItemSelect('SELECT_ITEM');
                              }}
                              onMouseEnter={() => setSelectedItemIndex(0)}
                              onMouseLeave={() => {
                                if (selectedItemIndex !== 0) {
                                  setSelectedItemIndex(-1);
                                }
                              }}
                              tabIndex={-1}
                            >
                              Select Item
                            </div>
                            {filteredItems.length > 0 ? (
                              <>
                                {filteredItems.map((item, index) => {
                                  const itemIndex = index + 1;
                                  const isSelected = selectedItemIndex === itemIndex;
                                  return (
                                    <div
                                      key={index}
                                      className="px-3 py-2 cursor-pointer"
                                      style={{
                                        cursor: 'pointer',
                                        borderBottom: '1px solid #f0f0f0',
                                        fontSize: '12px',
                                        wordWrap: 'break-word',
                                        whiteSpace: 'normal',
                                        backgroundColor: isSelected ? '#e3f2fd' : 'white',
                                        outline: isSelected ? '2px solid #1976d2' : 'none',
                                        outlineOffset: '-2px',
                                        touchAction: 'manipulation'
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleItemSelect(item);
                                      }}
                                      onTouchEnd={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleItemSelect(item);
                                      }}
                                      onMouseEnter={() => setSelectedItemIndex(itemIndex)}
                                      onMouseLeave={() => {
                                        if (!isSelected) {
                                          setSelectedItemIndex(-1);
                                        }
                                      }}
                                      tabIndex={-1}
                                    >
                                      {item}
                                    </div>
                                  );
                                })}
                              </>
                            ) : (
                              <div className="px-3 py-2 text-muted" style={{ fontSize: '12px' }}>
                                No items found
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>

              {/* Remarks Field */}
              <div className="col-md-12 mb-3">
                <label htmlFor="remarks">Remarks<span style={{ color: 'red' }}>*</span></label>
                <textarea
                  className="form-control"
                  id="remarks"
                  name="remarks"
                  value={remarksData.remarks || ''}
                  onChange={handleChange}
                  rows="4"
                  maxLength="1000"
                  placeholder="Enter Remarks..."
                  style={{ resize: 'vertical' }}
                />
                <div className="d-flex justify-content-between align-items-center mt-1">
                  <small className="text-muted">
                    <i className="fa fa-info-circle me-1"></i>
                    Remarks will hold 1000 characters
                  </small>
                  <small className="text-muted">
                    {remarksData.remarks?.length || 0}/1000
                  </small>
                </div>
                {showRemarksContextError && (
                  <small className="text-danger d-block mt-1">Please select Block and Item above when entering Remarks.</small>
                )}
              </div>
            </div>
          </div>
        </div>

    </>
  );
}

export default Remarks2;
